import Uppy, { UppyFile } from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import AwsS3 from "@uppy/aws-s3";
import GoldenRetriever from "@uppy/golden-retriever";
import DuplicateDetectionPlugin from "./DuplicateDetectionPlugin";
import Webcam from "@uppy/webcam";

// import Dropbox from '@uppy/dropbox';
import { generateS3Key, gssCreatePhoto, gssFetchS3Keys, gssGetUploadParams } from "./services";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";
import "./styles/index.css";

export type FileMeta = {
  albumId?: number;
  s3Key?: string;
};

export type FileBody = Record<string, unknown>;

export type UppyStruct = Uppy<FileMeta, Record<string, never>>;

export type CreateUppyProps = {
  albumId: number;
  hasVideoAccess: boolean;
  onDuplicateDecision?: (duplicates: string[]) => Promise<boolean>;
  mobileCameraOnlyUpload?: boolean;
};

function useS3Uploader(uppy: UppyStruct, albumId: number) {
  uppy.use(AwsS3, {
    limit: 6,
    shouldUseMultipart: false,
    getUploadParameters: async (file: UppyFile<FileMeta, FileBody>) => {
      const params = await gssGetUploadParams({
        albumId,
        filename: file.name || file.id,
        contentType: file.type || 'application/octet-stream',
      });
      uppy.setFileMeta(file.id, { s3Key: params.s3_key, albumId });
      return {
        method: 'PUT' as const,
        url: params.url,
        headers: params.headers,
      };
    },
  });
}

// To be used in JS/TS code w/o React
export function startUppy(props: {
  albumId: number;
  targetElementId: string;
}) {

  const uppy = new Uppy({
    restrictions: {
      maxFileSize: 250 * 1024 * 1024, // 250MB in bytes
      allowedFileTypes: null
    }
  }).use(Dashboard, {
    inline: true,
    target: "#" + props.targetElementId,
    plugins: ["Webcam"],
    proudlyDisplayPoweredByUppy: false
  });

  uppy.use(Webcam, {
    modes: ["picture", "video-audio", "video-only"],
    target: "#" + props.targetElementId,
    showRecordingLength: true,
    showVideoSourceDropdown: true
  });

  uppy.use(GoldenRetriever);

  useS3Uploader(uppy, props.albumId);

  uppy.use(DuplicateDetectionPlugin, {
    endpoint: "api/v1/photos/duplicate_check",
    albumId: props.albumId
  });

  uppy.on("upload-success", async (file, response: any) => {
    if (file === undefined) {
      console.error("Undefined file uploaded !!");
      return;
    }

    await gssCreatePhoto(file, props.albumId);
  });

  return uppy;
}

export function createUppy(props: CreateUppyProps): UppyStruct {
  const videoTypes = [
    "video/*",
    ".mp4",
    ".mov",
    ".avi",
    ".wmv",
    ".flv",
    ".mkv",
    ".webm"
  ];

  const imageTypes = [
    "image/*",
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".heic",
    ".heif"
  ];

  const allowedFileTypes = [...imageTypes, ...videoTypes];

  // Check if we're on mobile and restrictions are enabled
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const shouldRestrictToCamera = props.mobileCameraOnlyUpload && isMobile;

  const uppy: UppyStruct = new Uppy({
    id: `uppy-${props.albumId}`,
    restrictions: {
      maxFileSize: 250 * 1024 * 1024, // 250MB in bytes
      allowedFileTypes: allowedFileTypes
    }
  });

  uppy.setMeta({ albumId: props.albumId });

  uppy.use(Webcam, {
    modes: props.hasVideoAccess ? ["picture", "video-audio"] : ["picture"],
    showRecordingLength: props.hasVideoAccess,
    showVideoSourceDropdown: true,
    videoConstraints: {
      facingMode: { ideal: "environment" }
    },
    mobileNativeCamera: true,
    mirror: false
  });

  // Add S3 uploader (needed for all modes including webcam captures)
  useS3Uploader(uppy, props.albumId);

  uppy.use(DuplicateDetectionPlugin, {
    endpoint: "api/v1/photos/duplicate_check",
    albumId: props.albumId,
    onDuplicateDecision: props.onDuplicateDecision
  });

  // file validation filter
  if (!props.hasVideoAccess) {
    uppy.on("file-added", file => {
      const fileType = file.type;
      if (fileType?.startsWith("video/")) {
        uppy.removeFile(file.id);
        uppy.info(
          "Video uploads are not authorized for your account. Please contact support@geosnapshot.com for help.",
          "error",
          8000
        );
      }
    });
  }

  // Restrict file additions to camera only when mobile restricted
  if (shouldRestrictToCamera) {
    uppy.on("file-added", file => {
      // Allow files from Webcam plugin, reject all others
      if (file.source !== "Webcam") {
        uppy.removeFile(file.id);
        uppy.info(
          "On mobile devices, only camera capture is allowed for uploads.",
          "error",
          5000
        );
      }
    });
  }

  return uppy;
}
